import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StytchProvider } from '@stytch/react';
import { StytchUIClient } from '@stytch/vanilla-js';
import { AuthProvider } from '@/modules/auth/ui/AuthProvider';
import { useAuthContext } from '@/modules/auth/ui/AuthProvider';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
});
import AuthenticatePage from './pages/AuthenticatePage';
import LandingPage from './pages/LandingPage';
import AccountSettings from './pages/AccountSettings';
import AdminPortalHomepage from './pages/AdminPortalHomepage';
import AdminAppsPage from './pages/AdminAppsPage';
import AdminBuildQueuePage from './pages/AdminBuildQueuePage';
import AdminQuotesNeededPage from './pages/AdminQuotesNeededPage';
import AdminReadyToLivePage from './pages/AdminReadyToLivePage';
import AdminPaymentIssuesPage from './pages/AdminPaymentIssuesPage';
import AdminAppsNoPaymentMethodPage from './pages/AdminAppsNoPaymentMethodPage';
import AdminAppPageWrapper from './pages/admin/AdminAppPageWrapper';
import AdminAppOverviewPage from './pages/admin/AdminAppOverviewPage';
import AdminAppChargesPage from './pages/admin/AdminAppChargesPage';
import AdminAppSettingsPage from './pages/admin/AdminAppSettingsPage';
import AdminAppNotesPage from './pages/admin/AdminAppNotesPage';
import AdminAppConnectivityPage from './pages/admin/AdminAppConnectivityPage';
import AdminAppDevGuidePage from './pages/admin/AdminAppDevGuidePage';
import AdminCreateQuotePage from './pages/admin/AdminCreateQuotePage';
import AdminEmailTemplatesPage from './pages/admin/AdminEmailTemplatesPage';
import AdminErrorsPage from './pages/AdminErrorsPage';
import AdminErrorDetailPage from './pages/admin/AdminErrorDetailPage';
import AdminErrorsWrapper from './pages/admin/AdminErrorsWrapper';
import QuoteApprovalPage from './pages/QuoteApprovalPage';
import CrmDashboard from './pages/crm/CrmDashboard';
import CrmContactsPage from './pages/crm/CrmContactsPage';
import CrmContactDetailPage from './pages/crm/CrmContactDetailPage';
import CrmNewContactPage from './pages/crm/CrmNewContactPage';
import CrmOpportunitiesPage from './pages/crm/CrmOpportunitiesPage';
import CrmOpportunityDetailPage from './pages/crm/CrmOpportunityDetailPage';
import CrmNewOpportunityPage from './pages/crm/CrmNewOpportunityPage';
import CrmSettingsPage from './pages/crm/CrmSettingsPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import VisionPage from './pages/VisionPage';
import DashboardPage from './pages/DashboardPage';

// Main Apps page - primary entry point for viewing user apps
import AppDetailPage from './pages/AppDetailPage';
import CreateAppPage from './pages/CreateAppPage';
import AppSummaryPage from './pages/AppSummaryPage';
// AppSettings deprecated - functionality moved to AppDetailPage
// AppRepositoriesPage deprecated - functionality moved to AppDetailPage
// AddAppPage removed - ZAPT-only model, no external app adding

import LoadingScreen from '@/shared/components/LoadingScreen';
import PageViewTracker from '@/services/analytics/components/PageViewTracker';
import PWAInstallBanner from '@/shared/components/PWAInstallBanner';
import VersionChecker from '@/shared/components/VersionChecker';
import { Toaster } from 'sonner';

// Protected Route wrapper that redirects to login
function ProtectedRoute({ element, requireAuth = true }) {
  const { user } = useAuthContext();
  
  if (!user && requireAuth) {
    return <Navigate to="/authenticate" replace />;
  }
  
  return element;
}

// Redirect component for legacy billing route
function BillingRedirect() {
  const { appId } = useParams();
  return <Navigate to={`/admin-portal/app/${appId}/charges`} replace />;
}


function AppContent() {
  const { loading, user } = useAuthContext();
  const location = useLocation();
  
  // Public routes: skip logged-in-only chrome (e.g. PWA install banner)
  const publicRoutes = ['/', '/authenticate', '/privacy-policy', '/terms-of-service', '/pricing', '/about', '/vision'];
  const isPublicRoute = publicRoutes.includes(location.pathname);

  // Show loading screen if auth is loading
  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="h-full text-gray-900">
      <PageViewTracker />
      {/* PWA Install Banner - only show when logged in on protected routes */}
      {user && !isPublicRoute && <PWAInstallBanner />}
      <Routes>
        {/* Public routes - no organization data needed */}
        <Route 
          path="/" 
          element={<LandingPage />} 
        />
        <Route 
          path="/authenticate" 
          element={<AuthenticatePage />} 
        />
        <Route 
          path="/dashboard" 
          element={<ProtectedRoute element={<DashboardPage />} />} 
        />
        <Route 
          path="/create-app" 
          element={<ProtectedRoute element={<CreateAppPage />} />} 
        />
        <Route 
          path="/app-summary" 
          element={<ProtectedRoute element={<AppSummaryPage />} />} 
        />
        <Route 
          path="/app/:appId" 
          element={<ProtectedRoute element={<AppDetailPage />} />} 
        />
        <Route 
          path="/app/:appId/quote" 
          element={<ProtectedRoute element={<QuoteApprovalPage />} />} 
        />
        <Route 
          path="/privacy-policy" 
          element={<PrivacyPolicy />} 
        />
        <Route 
          path="/terms-of-service" 
          element={<TermsOfService />} 
        />
        <Route 
          path="/vision" 
          element={<VisionPage />} 
        />
        
        {/* Legacy organization routes - redirect to dashboard */}
        <Route 
          path="/org" 
          element={<ProtectedRoute element={<Navigate to="/dashboard" replace />} />} 
        />
        <Route 
          path="/org/:orgId" 
          element={<ProtectedRoute element={<Navigate to="/dashboard" replace />} />} 
        />
        <Route 
          path="/hub" 
          element={<ProtectedRoute element={<Navigate to="/dashboard" replace />} />} 
        />
        <Route 
          path="/org/setup" 
          element={<ProtectedRoute element={<Navigate to="/dashboard" replace />} />} 
        />
        <Route 
          path="/org/:orgId/settings" 
          element={<ProtectedRoute element={<Navigate to="/dashboard" replace />} />} 
        />
        <Route 
          path="/org/:orgId/billing" 
          element={<ProtectedRoute element={<Navigate to="/dashboard" replace />} />} 
        />
        <Route 
          path="/org/:orgId/complete-billing-info" 
          element={<ProtectedRoute element={<Navigate to="/dashboard" replace />} />} 
        />
        <Route 
          path="/org/:orgId/manage" 
          element={<ProtectedRoute element={<Navigate to="/dashboard" replace />} />} 
        />
        <Route 
          path="/org/:orgId/apps/plan" 
          element={<ProtectedRoute element={<Navigate to="/dashboard" replace />} />} 
        />
        <Route 
          path="/org/:orgId/request-build" 
          element={<ProtectedRoute element={<Navigate to="/app-summary" replace />} />} 
        />
        
        {/* Admin Portal */}
        <Route 
          path="/admin-portal" 
          element={user ? <AdminPortalHomepage /> : <Navigate to="/" replace />} 
        />
        <Route 
          path="/admin-portal/quotes-needed" 
          element={user ? <AdminQuotesNeededPage /> : <Navigate to="/" replace />} 
        />
        <Route 
          path="/admin-portal/queue" 
          element={user ? <AdminBuildQueuePage /> : <Navigate to="/" replace />} 
        />
        <Route 
          path="/admin-portal/ready-to-live" 
          element={user ? <AdminReadyToLivePage /> : <Navigate to="/" replace />} 
        />
        <Route 
          path="/admin-portal/payment-issues" 
          element={user ? <AdminPaymentIssuesPage /> : <Navigate to="/" replace />} 
        />
        <Route 
          path="/admin-portal/apps-no-payment-method" 
          element={user ? <AdminAppsNoPaymentMethodPage /> : <Navigate to="/" replace />} 
        />
        <Route 
          path="/admin-portal/email-templates" 
          element={user ? <AdminEmailTemplatesPage /> : <Navigate to="/" replace />} 
        />
        <Route 
          path="/admin-portal/errors" 
          element={user ? <AdminErrorsWrapper /> : <Navigate to="/" replace />}
        >
          <Route path=":errorId" element={<AdminErrorDetailPage />} />
          <Route index element={<AdminErrorsPage />} />
        </Route>
        <Route 
          path="/admin-portal/apps" 
          element={user ? <AdminAppsPage /> : <Navigate to="/" replace />} 
        />
        <Route 
          path="/admin-portal/app/:appId/create-quote" 
          element={user ? <AdminCreateQuotePage /> : <Navigate to="/" replace />} 
        />
        <Route 
          path="/admin-portal/app/:appId" 
          element={user ? <AdminAppPageWrapper /> : <Navigate to="/" replace />} 
        >
          <Route index element={<AdminAppOverviewPage />} />
          <Route path="charges" element={<AdminAppChargesPage />} />
          <Route path="settings" element={<AdminAppSettingsPage />} />
          <Route path="connectivity" element={<AdminAppConnectivityPage />} />
          <Route path="dev-guide" element={<AdminAppDevGuidePage />} />
          <Route path="notes" element={<AdminAppNotesPage />} />
        </Route>
        {/* Legacy billing route - redirect to charges */}
        <Route 
          path="/admin-portal/app/:appId/billing" 
          element={user ? <BillingRedirect /> : <Navigate to="/" replace />} 
        />
        <Route 
          path="/org/:orgId/quote/:requestId" 
          element={<ProtectedRoute element={<Navigate to="/dashboard" replace />} />} 
        />
        
        {/* CRM Routes */}
        <Route 
          path="/crm" 
          element={user ? <CrmDashboard /> : <Navigate to="/" replace />} 
        />
        <Route 
          path="/crm/contacts" 
          element={user ? <CrmContactsPage /> : <Navigate to="/" replace />} 
        />
        <Route 
          path="/crm/contacts/new" 
          element={user ? <CrmNewContactPage /> : <Navigate to="/" replace />} 
        />
        <Route 
          path="/crm/contacts/:contactId" 
          element={user ? <CrmContactDetailPage /> : <Navigate to="/" replace />} 
        />
        <Route 
          path="/crm/opportunities" 
          element={user ? <CrmOpportunitiesPage /> : <Navigate to="/" replace />} 
        />
        <Route 
          path="/crm/opportunities/new" 
          element={user ? <CrmNewOpportunityPage /> : <Navigate to="/" replace />} 
        />
        <Route 
          path="/crm/opportunities/:opportunityId" 
          element={user ? <CrmOpportunityDetailPage /> : <Navigate to="/" replace />} 
        />
        <Route 
          path="/crm/settings" 
          element={user ? <CrmSettingsPage /> : <Navigate to="/" replace />} 
        />
        
        {/* Account - doesn't need org data */}
        <Route 
          path="/account" 
          element={user ? <AccountSettings /> : <Navigate to="/" replace />} 
        />
        
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default function App() {
  const publicToken = import.meta.env.VITE_PUBLIC_STYTCH_PUBLIC_TOKEN;

  // Create StytchUIClient - uses defaults from dashboard configuration
  const stytchClient = new StytchUIClient(publicToken);

  return (
    <StytchProvider stytch={stytchClient}>
      <Router>
        <AuthProvider>
          <QueryClientProvider client={queryClient}>
            <AppContent />
            <VersionChecker />
            <Toaster />
          </QueryClientProvider>
        </AuthProvider>
      </Router>
    </StytchProvider>
  );
}